import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { filter, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';
import {
  ApiResponse,
  UserProfile,
  TopSellingServiceResponse,
  RevenueByServiceTypeResponse,
  RevenueTrendResponse,
  DashboardCustomerStatisticsApiResponse,
  CustomerTrendApiResponse,
  RevenueStatisticsApiResponse,
  ServiceHistoryRecord,
  PlanChangeHistoryResponse,
  VendorSubscriptionLookupData,
  VendorSubscriptionRedeemRequest,
  VendorMembershipLookupData,
  VendorMembershipLookupRequest,
  VehicleData,
  WashCountApiResponse,
  CampaignStatisticsApiResponse,
  CampaignLocationItem,
  CampaignOverallStatisticsApiResponse,
  CampaignFileUploadResponse,
} from '../shared/types';
import { getDefaultShopTimezone } from '../shared/utils/date.util';
import { getUserDataLaneCameraRoi, getUserDataLaneId } from '../shared/utils/auth-headers.util';
import {
  getActiveUserDataRaw,
  IMPERSONATION_USER_DATA_KEY,
  readActiveTunnelData,
  setImpersonationStorageItem,
} from '../shared/utils/impersonation-handoff.util';
import { isTunnelAppContext } from '../shared/utils/tunnel-app-context.util';

/** Allowed values for Angular HttpClient query-parameter maps. */
type HttpParamMap = Record<
  string,
  string | number | boolean | readonly (string | number | boolean)[]
>;

/**
 * Centralized API service that handles authenticated and unauthenticated HTTP requests, token and API-key header management, file uploads, and many application-specific endpoints (profile, appointments, revenue, campaigns, segments, etc.).
 * @component
 * @selector app-api-service
 * @example
 *   <app-api-service></app-api-service>
 * @input none none - This injectable service has no Angular input properties.
 * @output none none - This injectable service does not emit Angular output events; it returns Observables from methods instead.
 *
 * Purpose:
 * - Provide a single, typed place to perform GET/POST/PUT/DELETE requests against the API with automatic header handling, logging via LoggerService, and convenience wrappers for many domain endpoints.
 *
 * Key methods:
 * - get<T>(endpoint, params?) / post<T>(endpoint, data) / put<T>(endpoint, data) / delete<T>(endpoint):
 *     Generic HTTP methods that attach an Authorization header from localStorage when available and log API calls.
 * - postWithoutAuth<T>(endpoint, data):
 *     POST helper that does not include authentication (useful for login or public endpoints).
 * - getWithApiKey/postWithApiKey/putWithApiKey/deleteWithApiKey:
 *     Super-admin methods that use an API key fallback or a super-admin token stored in localStorage.
 * - getWithSuperAdminToken/postWithSuperAdminToken/putWithSuperAdminToken/deleteWithSuperAdminToken:
 *     Super-admin methods that require a stored token (no API key fallback) and will throw if the token is missing.
 * - getWithTunnelToken/deleteWithTunnelToken:
 *     Requests that use a "tunnel" token from localStorage when present.
 * - uploadCampaignFile(formData) / uploadLicensePlateImage(file):
 *     File upload helpers that intentionally avoid setting Content-Type for FormData so the browser sets the correct multipart boundary.
 * - Convenience domain helpers (resendEmail, logout, getProfile, updateProfile, getServiceHistory, getAppointmentHistory, revenue/campaign/customer statistics methods, getSegments, deleteSegment, getCampaignsByLocation, sendTestEmail, etc.)
 *
 * Important behaviors and lifecycle notes:
 * - This service is providedIn: 'root' and therefore has no Angular lifecycle hooks.
 * - Tokens are read from localStorage via parseLocalStorageToken and tunnelData parsing with robust JSON error handling (invalid JSON is removed and null is returned).
 * - For FormData uploads the service avoids setting Content-Type so the browser supplies the multipart boundary.
 * - getSuperAdminToken... methods will throw an error if a required super-admin token is not found.
 * - All API calls are logged via LoggerService with a best-effort status mapping for success/failure.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  /** Versioned API root — change `environment.apiVersion` to upgrade the whole app. */
  private baseUrl = `${environment.baseUrl}/${environment.apiVersion}`;
  private readonly vehicleHistoryCacheTtlMs = 2500;
  private vehicleHistoryInFlight = new Map<string, Observable<ApiResponse<any>>>();
  private vehicleHistoryRecent = new Map<string, { at: number; response: ApiResponse<any> }>();

  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) {}

  /**
   * Read a JSON string from localStorage by key and return its "token" property, or null if not present or invalid.
   * @example
   * parseLocalStorageToken('auth_item', 'Auth Item')
   * 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9'
   * @param {{string}} {{key}} - localStorage key that stores a JSON string containing a token property.
   * @param {{string}} {{label}} - Human-readable label used in error logging when JSON is invalid.
   * @returns {{string|null}} Return the token string if found and valid, otherwise null.
   **/
  private parseLocalStorageToken(key: string, label: string): string | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.token || null;
    } catch (error) {
      this.logger.error(`Invalid JSON in localStorage ${label}:`, error, 'ApiService');
      localStorage.removeItem(key);
      return null;
    }
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    // Tab-scoped impersonation (sessionStorage) via getActiveUserDataRaw; else shared userData.
    const raw = getActiveUserDataRaw();
    if (raw) {
      try {
        const token = JSON.parse(raw)?.token;
        if (token) {
          headers = headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        this.logger.error('Invalid JSON in active user session:', error, 'ApiService');
      }
    }
    const timezone = getDefaultShopTimezone();
    if (timezone) headers = headers.set('x-timezone', timezone);
    return headers;
  }

  private getHeadersWithoutContentType(): HttpHeaders {
    // Don't set Content-Type - let browser set it with boundary for FormData
    return this.getHeaders();
  }

  /**
   * Builds HTTP headers for super admin requests, using a bearer token when available or an API key as a fallback.
   * @example
   * getSuperAdminHeaders()
   * HttpHeaders with Authorization or api-key set
   * @returns {HttpHeaders} The headers to use for super admin API requests.
   **/
  private getSuperAdminHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    if (typeof window !== 'undefined') {
      const token = this.parseLocalStorageToken('superAdminData', 'superAdminData');
      if (token) {
        return headers.set('Authorization', `Bearer ${token}`);
      }
      // Fallback to API key if no token found (for login endpoint)
      headers = headers.set('api-key', environment.superAdminApiKey);
    }
    return headers;
  }

  private getSuperAdminHeadersWithoutContentType(): HttpHeaders {
    // Don't set Content-Type - let browser set it with boundary for FormData
    return this.getSuperAdminHeaders();
  }

  // Super Admin token-only headers (no API key fallback)
  private getSuperAdminTokenHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (typeof window !== 'undefined') {
      const token = this.parseLocalStorageToken('superAdminData', 'superAdminData');
      if (token) {
        return headers.set('Authorization', `Bearer ${token}`);
      }
      throw new Error('Super admin token not found');
    }
    return headers;
  }

  private getSuperAdminTokenHeadersWithoutContentType(): HttpHeaders {
    // Don't set Content-Type - let browser set it with boundary for FormData
    return this.getSuperAdminTokenHeaders();
  }

  get<T = any>(endpoint: string, params: HttpParamMap = {}): Observable<ApiResponse<T>> {
    this.logger.logApiCall('GET', endpoint);
    if (endpoint === 'user/payment-modes' && isTunnelAppContext()) {
      return of({ status: true, data: [] as T });
    }
    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getHeaders(),
        params: params,
      })
      .pipe(tap(response => this.logger.logApiCall('GET', endpoint, response.status ? 200 : 400)));
  }

  /**
   * Authenticated GET that returns a binary/file response (e.g. CSV export).
   * Progress events keep HttpTimeoutInterceptor's per-event budget alive while a long
   * download is still streaming (CWS-1637); only the final body is emitted.
   */
  getBlob(endpoint: string, params: HttpParamMap = {}): Observable<Blob> {
    this.logger.logApiCall('GET', endpoint);
    return this.http
      .get(`${this.baseUrl}/${endpoint}`, {
        headers: this.getHeaders(),
        params,
        responseType: 'blob',
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        filter((event): event is HttpResponse<Blob> => event.type === HttpEventType.Response),
        map(response => response.body ?? new Blob([])),
        tap(() => this.logger.logApiCall('GET', endpoint, 200))
      );
  }

  /** Authenticated POST that returns a binary/file response (e.g. XLSX export). */
  postBlob(endpoint: string, data: any): Observable<Blob> {
    this.logger.logApiCall('POST', endpoint);
    return this.http
      .post(`${this.baseUrl}/${endpoint}`, data, {
        headers: this.getHeaders(),
        responseType: 'blob',
      })
      .pipe(tap(() => this.logger.logApiCall('POST', endpoint, 200)));
  }

  getWithBody<T = any>(
    endpoint: string,
    body: any,
    params: HttpParamMap = {}
  ): Observable<ApiResponse<T>> {
    this.logger.logApiCall('GET', endpoint);
    return this.http
      .request<ApiResponse<T>>('GET', `${this.baseUrl}/${endpoint}`, {
        headers: this.getHeaders(),
        params,
        body,
      })
      .pipe(tap(response => this.logger.logApiCall('GET', endpoint, response.status ? 200 : 400)));
  }

  /**
   * Send a POST request to the API and return an observable of the API response; logs the call and handles FormData specially.
   * @example
   * post('users/create', { name: 'Alice' })
   * Observable<ApiResponse<any>>
   * @param {{string}} {{endpoint}} - Endpoint path relative to the configured baseUrl.
   * @param {{any}} {{data}} - Request payload; if FormData, the Content-Type header is not set so the browser can add the boundary.
   * @returns {{Observable<ApiResponse<T>>}} An Observable that emits the ApiResponse with payload typed as T.
   **/
  post<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('POST', endpoint);
    const headers = this.getHeaders();

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (data instanceof FormData) {
      return this.http
        .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400)));
  }

  // POST request without authentication (for public endpoints like login)
  postWithoutAuth<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('POST', endpoint);
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');

    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400)));
  }

  /** GET request without authentication (public campaign pages, etc.). */
  getWithoutAuth<T = any>(endpoint: string, params: HttpParamMap = {}): Observable<ApiResponse<T>> {
    this.logger.logApiCall('GET', endpoint);
    const headers = new HttpHeaders();

    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, { headers, params })
      .pipe(tap(response => this.logger.logApiCall('GET', endpoint, response.status ? 200 : 400)));
  }

  /**
   * Send a PUT request to the API endpoint and return an Observable of ApiResponse<T>.
   * @example
   * ut('users/123', { name: 'Alice' })
   * Observable<ApiResponse<User>>
   * @param {{string}} {{endpoint}} - The relative API endpoint path to send the PUT request to.
   * @param {{any}} {{data}} - The request payload; can be a plain object or FormData (FormData will omit the Content-Type header).
   * @returns {{Observable<ApiResponse<T>>}} Observable that emits the ApiResponse typed with the generic T.
   **/
  put<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('PUT', endpoint);
    const headers = this.getHeaders();

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (data instanceof FormData) {
      return this.http
        .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400)));
  }

  patch<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('PATCH', endpoint);
    const headers = this.getHeaders();

    if (data instanceof FormData) {
      return this.http
        .patch<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('PATCH', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .patch<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(
        tap(response => this.logger.logApiCall('PATCH', endpoint, response.status ? 200 : 400))
      );
  }

  delete<T = any>(endpoint: string): Observable<ApiResponse<T>> {
    this.logger.logApiCall('DELETE', endpoint);
    return this.http
      .delete<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, { headers: this.getHeaders() })
      .pipe(
        tap(response => this.logger.logApiCall('DELETE', endpoint, response.status ? 200 : 400))
      );
  }

  // Super Admin API methods (using API key instead of token)
  getWithApiKey<T = any>(endpoint: string, params: HttpParamMap = {}): Observable<ApiResponse<T>> {
    this.logger.logApiCall('GET', endpoint);
    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getSuperAdminHeaders(),
        params: params,
      })
      .pipe(tap(response => this.logger.logApiCall('GET', endpoint, response.status ? 200 : 400)));
  }

  /**
   * Sends a POST request to the given endpoint using a super-admin API key and returns an observable of the ApiResponse.
   * @example
   * ostWithApiKey('users/create', { name: 'Alice' })
   * Observable<ApiResponse<any>>
   * @param {{string}} {{endpoint}} - Relative endpoint path to POST to.
   * @param {{any}} {{data}} - Request payload; if FormData is provided, Content-Type header is omitted.
   * @returns {{Observable<ApiResponse<T>>}} Observable that emits the API response and logs the API call result.
   **/
  postWithApiKey<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('POST', endpoint);
    const headers = this.getSuperAdminHeaders();

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (data instanceof FormData) {
      return this.http
        .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getSuperAdminHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400)));
  }

  /**
   * Perform a PUT request to the specified endpoint using super-admin API key headers; preserves FormData without setting Content-Type so the browser can add the boundary.
   * @example
   * utWithApiKey('/users/123', formData)
   * Observable<ApiResponse<User>>
   * @param {{string}} {{endpoint}} - The endpoint path (relative to baseUrl) to send the PUT request to.
   * @param {{any}} {{data}} - The request payload; may be a FormData instance or a serializable object.
   * @returns {{Observable<ApiResponse<T>>}} Return an observable that emits the API response typed by generic T.
   **/
  putWithApiKey<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('PUT', endpoint);
    const headers = this.getSuperAdminHeaders();

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (data instanceof FormData) {
      return this.http
        .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getSuperAdminHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400)));
  }

  deleteWithApiKey<T = any>(endpoint: string): Observable<ApiResponse<T>> {
    this.logger.logApiCall('DELETE', endpoint);
    return this.http
      .delete<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getSuperAdminHeaders(),
      })
      .pipe(
        tap(response => this.logger.logApiCall('DELETE', endpoint, response.status ? 200 : 400))
      );
  }

  // Super Admin API methods using token only (no API key fallback)
  getWithSuperAdminToken<T = any>(
    endpoint: string,
    params: HttpParamMap = {}
  ): Observable<ApiResponse<T>> {
    this.logger.logApiCall('GET', endpoint);
    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getSuperAdminTokenHeaders(),
        params: params,
      })
      .pipe(tap(response => this.logger.logApiCall('GET', endpoint, response.status ? 200 : 400)));
  }

  /** Super-admin GET that returns a binary/file response (e.g. CSV template download). */
  getBlobWithSuperAdminToken(endpoint: string, params: HttpParamMap = {}): Observable<Blob> {
    this.logger.logApiCall('GET', endpoint);
    return this.http
      .get(`${this.baseUrl}/${endpoint}`, {
        headers: this.getSuperAdminTokenHeaders(),
        params,
        responseType: 'blob',
      })
      .pipe(tap(() => this.logger.logApiCall('GET', endpoint, 200)));
  }

  /**
   * Post data to a backend endpoint using a Super Admin token, logging the API call and correctly handling FormData.
   * @example
   * ostWithSuperAdminToken('users/create', { name: 'Alice' })
   * Observable<ApiResponse<{ id: number }>>
   * @param {{string}} {{endpoint}} - Endpoint path relative to the service baseUrl.
   * @param {{any}} {{data}} - Request payload; can be FormData (Content-Type omitted) or any other serializable data.
   * @returns {{Observable<ApiResponse<T>>}} Return Observable that emits the API response wrapped in ApiResponse<T>.
   **/
  postWithSuperAdminToken<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('POST', endpoint);
    const headers = this.getSuperAdminTokenHeaders();

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (data instanceof FormData) {
      return this.http
        .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getSuperAdminTokenHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400)));
  }

  /**
   * Send a PUT request to the specified endpoint using Super Admin credentials; omits Content-Type for FormData and logs the API call.
   * @example
   * utWithSuperAdminToken('users/123', { name: 'Alice' })
   * Observable<ApiResponse<any>>
   * @param {{string}} {{endpoint}} - The API endpoint path (relative to the service baseUrl) to send the PUT request to.
   * @param {{any}} {{data}} - The request payload; may be a plain object or a FormData instance (FormData will omit Content-Type so the browser can set the boundary).
   * @returns {{Observable<ApiResponse<T>>}} Observable that emits the API response typed with the generic T.
   **/
  putWithSuperAdminToken<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('PUT', endpoint);
    const headers = this.getSuperAdminTokenHeaders();

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (data instanceof FormData) {
      return this.http
        .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getSuperAdminTokenHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400)));
  }

  patchWithSuperAdminToken<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('PATCH', endpoint);
    const headers = this.getSuperAdminTokenHeaders();

    if (data instanceof FormData) {
      return this.http
        .patch<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
          headers: this.getSuperAdminTokenHeadersWithoutContentType(),
        })
        .pipe(
          tap(response => this.logger.logApiCall('PATCH', endpoint, response.status ? 200 : 400))
        );
    }

    return this.http
      .patch<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, { headers })
      .pipe(
        tap(response => this.logger.logApiCall('PATCH', endpoint, response.status ? 200 : 400))
      );
  }

  deleteWithSuperAdminToken<T = any>(endpoint: string): Observable<ApiResponse<T>> {
    this.logger.logApiCall('DELETE', endpoint);
    return this.http
      .delete<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getSuperAdminTokenHeaders(),
      })
      .pipe(
        tap(response => this.logger.logApiCall('DELETE', endpoint, response.status ? 200 : 400))
      );
  }

  /**
   * Get HTTP headers for tunnel requests, including an Authorization header when a valid tunnel token is found in localStorage.
   * @example
   * getTunnelHeaders()
   * // HttpHeaders with Authorization: 'Bearer <token>' if tunnelData.token exists, otherwise an empty HttpHeaders instance
   * @returns {HttpHeaders} HttpHeaders instance that may contain the Authorization header when a stored tunnel token is present.
   */
  private getTunnelHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    if (typeof window !== 'undefined') {
      const token = readActiveTunnelData()?.['token'];
      if (typeof token === 'string' && token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return headers;
  }

  getWithTunnelToken<T = any>(
    endpoint: string,
    params: HttpParamMap = {}
  ): Observable<ApiResponse<T>> {
    this.logger.logApiCall('GET', endpoint);
    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getTunnelHeaders(),
        params: params,
      })
      .pipe(tap(response => this.logger.logApiCall('GET', endpoint, response.status ? 200 : 400)));
  }

  postWithTunnelToken<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('POST', endpoint);
    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
        headers: this.getTunnelHeaders(),
      })
      .pipe(tap(response => this.logger.logApiCall('POST', endpoint, response.status ? 200 : 400)));
  }

  putWithTunnelToken<T = any>(endpoint: string, data: any): Observable<ApiResponse<T>> {
    this.logger.logApiCall('PUT', endpoint);
    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, data, {
        headers: this.getTunnelHeaders(),
      })
      .pipe(tap(response => this.logger.logApiCall('PUT', endpoint, response.status ? 200 : 400)));
  }

  deleteWithTunnelToken<T = any>(endpoint: string): Observable<ApiResponse<T>> {
    this.logger.logApiCall('DELETE', endpoint);
    return this.http
      .delete<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getTunnelHeaders(),
      })
      .pipe(
        tap(response => this.logger.logApiCall('DELETE', endpoint, response.status ? 200 : 400))
      );
  }

  resendEmail(data: { email: string }): Observable<ApiResponse<void>> {
    return this.post('user/resend-verification-email', data);
  }

  logout(): Observable<ApiResponse<void>> {
    return this.post('user/auth/logout', {});
  }

  getProfile(): Observable<ApiResponse<UserProfile>> {
    return this.get('user/me');
  }

  updateProfile(profileData: any): Observable<ApiResponse<UserProfile>> {
    return this.put('user/me', profileData);
  }

  updateSelectedLane(
    laneId: string | null
  ): Observable<ApiResponse<{ selected_lane_id: string | null }>> {
    return this.patch('user/me/selected-lane', { lane_id: laneId });
  }

  getServiceHistory(customerId: string): Observable<ApiResponse<ServiceHistoryRecord[]>> {
    return this.get(`customer/${customerId}/service-history`);
  }

  /**
   * CWS-1444: every membership plan change this customer has made, across all of
   * their memberships. Merged into the Service History tab so a member's record
   * reads as one story rather than two.
   */
  getCustomerPlanChangeHistory(
    customerId: string
  ): Observable<ApiResponse<PlanChangeHistoryResponse>> {
    return this.get(`order/membership-plan-change/customers/${customerId}/changes`);
  }

  lookupVendorSubscription(
    sourceId: string,
    source?: string
  ): Observable<ApiResponse<VendorSubscriptionLookupData>> {
    const params: HttpParamMap = { source_id: sourceId };
    if (source) {
      params['source'] = source;
    }
    return this.get('customer/vendor-subscription-lookup', params);
  }

  redeemVendorSubscription(
    payload: VendorSubscriptionRedeemRequest
  ): Observable<ApiResponse<VendorSubscriptionLookupData>> {
    return this.post('order/vendor-subscription-redeem', payload);
  }

  /**
   * CWS-1266: attendant asks the vendor (DRB) about a refused LPR entry. One or two
   * vendor round-trips, so callers should show progress for ~3-10 s.
   */
  lookupVendorMembershipForLprEntry(
    lprEntryId: string,
    payload: VendorMembershipLookupRequest
  ): Observable<ApiResponse<VendorMembershipLookupData>> {
    return this.post(
      `order/lpr-entries/${encodeURIComponent(lprEntryId)}/vendor-membership-lookup`,
      payload
    );
  }

  getVehicleOrderHistory(
    vehicleId: string,
    params: {
      page?: number;
      limit?: number;
      include_rewash_eligibility?: boolean;
      history_types?: string | string[];
      exclude_subscription_wash_id?: string;
    } = {}
  ): Observable<ApiResponse<any>> {
    this.cleanupVehicleHistoryRecentCache();
    const query: HttpParamMap = {};
    const page = params.page ?? 1;
    const limit = params.limit ?? 5;
    query['page'] = page;
    query['limit'] = limit;
    if (params.include_rewash_eligibility) {
      query['include_rewash_eligibility'] = true;
    }
    if (params.history_types != null && params.history_types !== '') {
      query['history_types'] = Array.isArray(params.history_types)
        ? params.history_types.join(',')
        : params.history_types;
    }
    if (params.exclude_subscription_wash_id) {
      query['exclude_subscription_wash_id'] = params.exclude_subscription_wash_id;
    }
    const rewashSuffix = params.include_rewash_eligibility ? '::rewash' : '';
    const typesSuffix = query['history_types'] ? `::types:${query['history_types']}` : '';
    const excludeSuffix = params.exclude_subscription_wash_id
      ? `::ex:${params.exclude_subscription_wash_id}`
      : '';
    const requestKey = `${String(vehicleId || '').trim()}::${page}::${limit}${rewashSuffix}${typesSuffix}${excludeSuffix}`;
    const now = Date.now();

    const recent = this.vehicleHistoryRecent.get(requestKey);
    if (recent && now - recent.at < this.vehicleHistoryCacheTtlMs) {
      return of(recent.response);
    }

    const inFlight = this.vehicleHistoryInFlight.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this.get(`order/vehicle/${vehicleId}/history`, query).pipe(
      tap(response => {
        this.vehicleHistoryRecent.set(requestKey, { at: Date.now(), response });
      }),
      finalize(() => {
        this.vehicleHistoryInFlight.delete(requestKey);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.vehicleHistoryInFlight.set(requestKey, request$);
    return request$;
  }

  private cleanupVehicleHistoryRecentCache(): void {
    const now = Date.now();
    for (const [key, value] of this.vehicleHistoryRecent.entries()) {
      if (now - value.at >= this.vehicleHistoryCacheTtlMs) {
        this.vehicleHistoryRecent.delete(key);
      }
    }
  }

  getAppointmentHistory(customerId: string): Observable<ApiResponse<any[]>> {
    return this.get(`customer/${customerId}/appointment-history`);
  }

  private buildRevenueFilterQuery(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): string {
    let query = `filter_by=${filterBy}&location_id=${locationId}`;
    if (timezone) {
      query += `&timezone=${encodeURIComponent(timezone)}`;
    }
    if (filterBy === 'custom') {
      if (customDates?.startDate) {
        query += `&start_date=${encodeURIComponent(customDates.startDate)}`;
      }
      if (customDates?.endDate) {
        query += `&end_date=${encodeURIComponent(customDates.endDate)}`;
      }
    }
    return query;
  }

  getRevenueStatistics(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueStatisticsApiResponse>> {
    return this.get(
      `revenue/get-revenue-statistics?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getTopSellingServices(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<TopSellingServiceResponse>> {
    return this.get(
      `revenue/get-top-selling-services?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getRevenueByServiceType(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueByServiceTypeResponse>> {
    return this.get(
      `revenue/get-revenue-by-service-type?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getRevenueTrend(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueTrendResponse>> {
    return this.get(
      `revenue/get-revenue-trend?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getAverageRevenueTrend(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueTrendResponse>> {
    return this.get(
      `revenue/get-average-revenue-trend?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getCustomerStatistics(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<DashboardCustomerStatisticsApiResponse>> {
    return this.get(
      `revenue/get-customer-statistics?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getCustomerVisitFrequency(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueByServiceTypeResponse>> {
    return this.get(
      `revenue/get-customer-visit-frequency?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getCustomerTrend(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<CustomerTrendApiResponse>> {
    return this.get(
      `revenue/get-customer-trend?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getWashesByServiceType(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueByServiceTypeResponse>> {
    return this.get(
      `revenue/get-washes-by-service-type?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getWashCountTrend(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueTrendResponse>> {
    return this.get(
      `revenue/get-wash-count-trend?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getWashCount(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<WashCountApiResponse>> {
    return this.get(
      `revenue/get-wash-count?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  getCampaignStatistics(
    filterBy: string,
    locationId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<CampaignStatisticsApiResponse>> {
    return this.get(
      `revenue/get-campaign-statistics?${this.buildRevenueFilterQuery(filterBy, locationId, timezone, customDates)}`
    );
  }

  /**
   * Fetches the current employee's own profile and normalises the `data` field to always be
   * the flat profile object `{ location_id, first_name, … }` regardless of whether the backend
   * wraps it in an extra `{ data: { … } }` envelope.
   */
  getCurrentEmployeeProfile(): Observable<ApiResponse<any>> {
    return this.get('employee/profile').pipe(
      map((res: ApiResponse<any>) => ({
        ...res,
        data: this.extractEmployeeProfile(res?.data),
      })),
      tap((res: ApiResponse<any>) => this.cacheEmployeeProfileTimezone(res?.data))
    );
  }

  // TODO: type with EmployeeRecord[] once the employee module types are stabilised
  getEmployeesByLocation(locationId: string): Observable<ApiResponse<any>> {
    return this.get(`employee/location/${locationId}`).pipe(
      map((res: ApiResponse<any>) => ({
        ...res,
        data: this.extractEmployeesArray(res?.data),
      }))
    );
  }

  /** Supports legacy `data: Employee[]` and paginated `{ data: Employee[], pagination }`. */
  private extractEmployeesArray(payload: any): any[] {
    if (payload == null) {
      return [];
    }
    if (Array.isArray(payload)) {
      return payload;
    }
    if (typeof payload === 'object' && Array.isArray((payload as { data?: any }).data)) {
      return (payload as { data: any[] }).data;
    }
    return [];
  }

  /**
   * Supports flat `data: { location_id }` and nested `data: { data: { location_id } }` employee profile shapes.
   * Returns the inner profile object that contains `location_id`.
   */
  private extractEmployeeProfile(payload: any): Record<string, any> {
    if (payload == null || typeof payload !== 'object') {
      return {};
    }
    const p = payload as Record<string, any>;
    // nested: { data: { location_id, ... } }
    if (p['data'] != null && typeof p['data'] === 'object' && !Array.isArray(p['data'])) {
      return p['data'] as Record<string, any>;
    }
    // flat: already the profile object
    return p;
  }

  private cacheEmployeeProfileTimezone(profile: any): void {
    if (typeof window === 'undefined' || !profile || typeof profile !== 'object') {
      return;
    }

    const timezone = this.extractTimezone(profile as Record<string, any>);
    if (!timezone) {
      return;
    }

    try {
      const raw = getActiveUserDataRaw();
      if (!raw) {
        return;
      }
      const userData = JSON.parse(raw);
      if (userData?.impersonated) {
        setImpersonationStorageItem(
          IMPERSONATION_USER_DATA_KEY,
          JSON.stringify({ ...userData, timezone })
        );
      } else {
        localStorage.setItem('userData', JSON.stringify({ ...userData, timezone }));
      }
    } catch (error) {
      this.logger.error('Error caching employee profile timezone', error, 'ApiService');
    }
  }

  private extractTimezone(profile: Record<string, any>): string {
    const timezone =
      profile['timezone'] ??
      (typeof profile['location'] === 'object' && profile['location'] !== null
        ? (profile['location'] as Record<string, any>)['timezone']
        : undefined) ??
      (typeof profile['location_details'] === 'object' && profile['location_details'] !== null
        ? (profile['location_details'] as Record<string, any>)['timezone']
        : undefined);

    return typeof timezone === 'string' ? timezone.trim() : '';
  }

  // TODO: type availability response once the appointment API contract is confirmed
  checkAvailability(data: {
    location_id: string;
    employee_id: string;
    appointment_time: string;
    duration: string;
  }): Observable<ApiResponse<any>> {
    return this.post('appointment/check-availability', data);
  }

  // TODO: type with Segment[] once the segment module types are stabilised
  getSegments(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {}
  ): Observable<ApiResponse<any>> {
    return this.get('segment', params);
  }

  deleteSegment(segmentId: string): Observable<ApiResponse<void>> {
    return this.delete(`segment/${segmentId}`);
  }

  // TODO: type with Segment once the segment module types are stabilised
  getSegmentById(segmentId: string): Observable<ApiResponse<any>> {
    return this.get(`segment/${segmentId}`);
  }

  // Upload file method for campaign file uploads
  uploadCampaignFile(formData: FormData): Observable<ApiResponse<CampaignFileUploadResponse>> {
    this.logger.logApiCall('POST', 'campaign/upload-file');
    return this.http
      .post<ApiResponse<CampaignFileUploadResponse>>(
        `${this.baseUrl}/campaign/upload-file`,
        formData,
        {
          headers: this.getHeadersWithoutContentType(),
        }
      )
      .pipe(
        tap(response =>
          this.logger.logApiCall('POST', 'campaign/upload-file', response.status ? 200 : 400)
        )
      );
  }

  // Send test email method
  sendTestEmail(data: any): Observable<ApiResponse<void>> {
    return this.post('campaign/test-email', data);
  }

  // Get campaigns by location
  getCampaignsByLocation(locationId: string): Observable<ApiResponse<CampaignLocationItem[]>> {
    return this.get(`campaign/location/${locationId}`);
  }

  private buildCampaignFilterQuery(
    platform: string,
    locationId: string,
    filterBy: string,
    campaignId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): string {
    let query = `platform=${platform}&location_id=${locationId}&filter_by=${filterBy}&campaign_id=${campaignId}`;
    if (timezone) {
      query += `&timezone=${encodeURIComponent(timezone)}`;
    }
    if (filterBy === 'custom') {
      if (customDates?.startDate) {
        query += `&start_date=${encodeURIComponent(customDates.startDate)}`;
      }
      if (customDates?.endDate) {
        query += `&end_date=${encodeURIComponent(customDates.endDate)}`;
      }
    }
    return query;
  }

  // Get campaign performance data
  getCampaignPerformance(
    platform: string,
    locationId: string,
    filterBy: string,
    campaignId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueByServiceTypeResponse>> {
    return this.get(
      `revenue/campaign-performance?${this.buildCampaignFilterQuery(platform, locationId, filterBy, campaignId, timezone, customDates)}`
    );
  }

  // Get campaign funnel data
  getCampaignFunnel(
    platform: string,
    locationId: string,
    filterBy: string,
    campaignId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<RevenueByServiceTypeResponse>> {
    return this.get(
      `revenue/campaign-funnel?${this.buildCampaignFilterQuery(platform, locationId, filterBy, campaignId, timezone, customDates)}`
    );
  }

  // Get campaign overall statistics
  getCampaignOverallStatistics(
    platform: string,
    locationId: string,
    filterBy: string,
    campaignId: string,
    timezone?: string,
    customDates?: { startDate?: string; endDate?: string }
  ): Observable<ApiResponse<CampaignOverallStatisticsApiResponse>> {
    return this.get(
      `revenue/campaign-overall-statistics?${this.buildCampaignFilterQuery(platform, locationId, filterBy, campaignId, timezone, customDates)}`
    );
  }

  // Upload license plate image
  /**
   * Uploads a license plate image file to the server endpoint '/order/get-lp-image' and returns the API response as an observable.
   * @example
   * ploadLicensePlateImage(file)
   * Observable<ApiResponse<any>>
   * @param {{File}} {{file}} - The image file containing the license plate to upload.
   * @returns {{Observable<ApiResponse<any>>}} An Observable that emits the ApiResponse returned by the server.
   **/
  uploadLicensePlateImage(file: File): Observable<ApiResponse<VehicleData>> {
    this.logger.logApiCall('POST', 'order/get-lp-image');
    const formData = new FormData();
    formData.append('image', file, file.name);
    const laneId = getUserDataLaneId();
    if (laneId) {
      formData.append('lane_id', laneId);
    }
    const cameraRoi = getUserDataLaneCameraRoi();
    if (cameraRoi != null) {
      formData.append('camera_roi', String(cameraRoi));
    }

    return this.http
      .post<ApiResponse<VehicleData>>(`${this.baseUrl}/order/get-lp-image`, formData, {
        headers: this.getHeadersWithoutContentType(),
      })
      .pipe(
        tap(response =>
          this.logger.logApiCall('POST', 'order/get-lp-image', response.status ? 200 : 400)
        )
      );
  }
}
